import type {PropsWithChildren} from 'react';
import {toPlainText} from '@portabletext/react';
import clsx from 'clsx';
import Link from 'next/link';

import {Skeleton} from '@/modules/finkn/components/skeleton';
import {Heading} from '@/modules/foundation/components/typography/heading';
import {internalLinkToHref} from '@/modules/foundation/sanity/link/query';
import {PortableText} from '@/modules/foundation/sanity/portable-text';
import {
	createPhoneLink,
	formatPhoneNumber,
} from '@/modules/foundation/shared/format';
import type {DocumentsByIdsQueryResult} from '@/sanity/schemas/types';

import {FaqItem} from './faq-item';

type Props = {
	data: DocumentsByIdsQueryResult | null;
	loading: boolean;
};

const LoadingDocument = () => {
	return (
		<div className="space-y-1">
			<Skeleton className={clsx('h-[30px]', 'w-1/2')} />
			<Skeleton className={clsx('h-[20px]')} />
			<Skeleton className={clsx('h-[20px]')} />
			<Skeleton className={clsx('h-[20px]', 'w-2/3')} />
		</div>
	);
};

const ResultHeading = ({children}: PropsWithChildren) => {
	return (
		<Heading level={2} size="md" spacing>
			{children}
		</Heading>
	);
};

const ListItem = ({children}: PropsWithChildren) => {
	return (
		<li className={clsx('border-b', 'border-grey-border', 'mb-6', 'pb-6')}>
			{children}
		</li>
	);
};

export const ResultsList = ({data, loading}: Props) => {
	return (
		<>
			{loading ? (
				<div className="space-y-20">
					<LoadingDocument />
					<LoadingDocument />
					<LoadingDocument />
				</div>
			) : (
				<ol>
					{data?.map((document) => {
						if (document._type === 'person') {
							return (
								<ListItem key={document._id}>
									<ResultHeading>
										{document.name}
									</ResultHeading>
									{document.company && (
										<p>{document.company}</p>
									)}
									{document.role && <p>{document.role}</p>}
									{document.email && <p>{document.email}</p>}
									{document.phone && (
										<a
											href={createPhoneLink(
												document.phone,
											)}
										>
											{formatPhoneNumber(document.phone)}
										</a>
									)}
								</ListItem>
							);
						} else if (document._type === 'faq') {
							return (
								<ListItem key={document._id}>
									<FaqItem question={document.question ?? ''}>
										{document.answer &&
										document.answer.length > 0 ? (
											<PortableText
												value={document.answer}
											/>
										) : null}
									</FaqItem>
								</ListItem>
							);
						} else if (document._type === 'article') {
							return (
								<ListItem key={document._id}>
									<Link
										href={internalLinkToHref(
											document._type,
											document.slug?.current ?? '',
										)}
										className={clsx(
											'hover:text-primary-15',
											'transition-colors',
											'inline-block',
										)}
									>
										<ResultHeading>
											{document.title}
										</ResultHeading>
										{document.lead &&
											document.lead.length > 0 && (
												<p
													className={clsx(
														'line-clamp-3',
														'text-primary-2',
													)}
												>
													{toPlainText(document.lead)}
												</p>
											)}
									</Link>
								</ListItem>
							);
						} else if (document._type === 'nemnd') {
							return (
								<ListItem key={document._id}>
									<Link
										href={internalLinkToHref(
											document._type,
											document.slug?.current ?? '',
										)}
										className={clsx(
											'hover:text-primary-15',
											'transition-colors',
											'inline-block',
										)}
									>
										<ResultHeading>
											{document.name}
										</ResultHeading>
										{document.description &&
											document.description.length > 0 && (
												<p
													className={clsx(
														'line-clamp-3',
														'text-primary-2',
													)}
												>
													{toPlainText(
														document.description,
													)}
												</p>
											)}
									</Link>
								</ListItem>
							);
						} else if (
							document._type === 'page' ||
							document._type === 'collection-area' ||
							document._type === 'about-page' ||
							document._type === 'bank-area' ||
							document._type === 'bank-guidebook' ||
							document._type === 'collection-guidebook' ||
							document._type === 'insurance-area' ||
							document._type === 'insurance-guidebook' ||
							document._type === 'finance-page'
						) {
							return (
								<ListItem key={document._id}>
									<Link
										href={internalLinkToHref(
											document._type,
											document.slug?.current ?? '',
										)}
										className={clsx(
											'hover:text-primary-15',
											'transition-colors',
											'inline-block',
										)}
									>
										<ResultHeading>
											{document.title}
										</ResultHeading>
										{document.pageHeadings?.[0].lead &&
											document.pageHeadings?.[0].lead
												.length > 0 && (
												<p
													className={clsx(
														'line-clamp-3',
														'text-primary-2',
													)}
												>
													{toPlainText(
														document.pageHeadings[0]
															.lead,
													)}
												</p>
											)}
									</Link>
								</ListItem>
							);
						} else if (
							document._type === 'about-frontpage' ||
							document._type === 'faq-frontpage' ||
							document._type === 'article-frontpage' ||
							document._type === 'bank-frontpage' ||
							document._type === 'collection-frontpage' ||
							document._type === 'companies-frontpage' ||
							document._type === 'insurance-frontpage' ||
							document._type === 'nemnd-frontpage' ||
							document._type === 'finance-frontpage' ||
							document._type === 'login-frontpage' ||
							document._type === 'frontpage'
						) {
							return (
								<ListItem key={document._id}>
									<Link
										href={internalLinkToHref(
											document._type,
										)}
										className={clsx(
											'hover:text-primary-15',
											'transition-colors',
											'inline-block',
										)}
									>
										<ResultHeading>
											{document.title}
										</ResultHeading>
										{document.pageHeadings?.[0].lead &&
											document.pageHeadings?.[0].lead
												.length > 0 && (
												<p
													className={clsx(
														'line-clamp-3',
														'text-primary-2',
													)}
												>
													{toPlainText(
														document.pageHeadings[0]
															.lead,
													)}
												</p>
											)}
									</Link>
								</ListItem>
							);
						} else if (
							document._type === 'complaintGuide-frontpage'
						) {
							return (
								<ListItem key={document._id}>
									<Link
										href={internalLinkToHref(
											document._type,
										)}
										className={clsx(
											'hover:text-primary-15',
											'transition-colors',
											'inline-block',
										)}
									>
										<ResultHeading>
											{document.title}
										</ResultHeading>
										{document.lead &&
											document.lead.length > 0 && (
												<p
													className={clsx(
														'line-clamp-3',
														'text-primary-2',
													)}
												>
													{toPlainText(document.lead)}
												</p>
											)}
									</Link>
								</ListItem>
							);
						}
					})}
				</ol>
			)}
		</>
	);
};
